import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, Paper, Typography, Card, CardContent,
  CircularProgress, Button, Chip, Divider
} from '@mui/material';
import {
  ShoppingCart as OrderIcon,
  AttachMoney as MoneyIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CheckIcon,
  Pending as PendingIcon,
  TrendingUp as TrendingIcon,
  Refresh as RefreshIcon,
  Rocket as RocketIcon,
  Warning as WarningIcon,
  Inventory2 as InventoryIcon,
  Launch as LaunchIcon,
  Storefront as StorefrontIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

const StatCard = ({ title, value, icon, color, subtitle, onClick }) => (
  <Card
    sx={{
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': onClick ? {
        transform: 'translateY(-4px)',
        boxShadow: 4
      } : {}
    }}
    onClick={onClick}
  >
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography color="text.secondary" variant="body2" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold" color={color || 'text.primary'}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{
          p: 1.5,
          borderRadius: 2,
          bgcolor: `${color || 'primary'}.lighter`,
          color: color || 'primary.main'
        }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const AdminDashboard = () => {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_orders: 0,
    pending_orders: 0,
    paid_orders: 0,
    shipping_orders: 0,
    delivered_orders: 0,
    total_revenue: 0,
    today_orders: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, ordersRes, billingRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/orders?limit=5'),
        api.get('/billing/current').catch(() => null)
      ]);

      if (statsRes.data.success) {
        setStats(statsRes.data.stats);
      }
      if (ordersRes.data.success) {
        setRecentOrders(ordersRes.data.orders);
      }
      if (billingRes?.data?.success) {
        setBilling(billingRes.data.billing);
      }
    } catch (error) {
      toast.error(t('dashboard.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  const statusColors = {
    pending: 'warning',
    paid: 'info',
    shipping: 'primary',
    delivered: 'success',
    cancelled: 'error'
  };

  const statusLabels = {
    pending: t('orders.status_pending'),
    paid: t('orders.status_paid'),
    shipping: t('orders.status_shipping', t('orders.status_shipped')),
    delivered: t('orders.status_delivered'),
    cancelled: t('orders.status_cancelled')
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            {t('dashboard.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('dashboard.welcome')}
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={fetchData}>
          {t('common.refresh')}
        </Button>
      </Box>

      {/* 好批專屬經銷商就緒商店迎賓與新手圖文引導 */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          background: 'linear-gradient(135deg, #0F1E2E 0%, #1F3A5F 50%, #2C8C8C 100%)',
          color: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 10px 30px rgba(31,58,95,0.18)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2.5 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip
                  label="★ 超級好批 VIP 經銷特權・開箱即賣 ★"
                  size="small"
                  sx={{ bgcolor: 'rgba(253, 224, 71, 0.2)', color: '#fde047', fontWeight: 'bold', border: '1px solid rgba(253, 224, 71, 0.4)' }}
                />
                <Chip
                  label="已連線 2,710+ 款商品庫存"
                  size="small"
                  sx={{ bgcolor: 'rgba(255, 255, 255, 0.15)', color: '#ffffff' }}
                />
              </Box>
              <Typography variant="h5" fontWeight="900" sx={{ mb: 0.5, letterSpacing: 0.5 }}>
                🎉 歡迎來到您的好批就緒旗艦店！商品已預載，立即開始接單！
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', maxWidth: 720, lineHeight: 1.6 }}>
                好批已為您預先上架 12 件當季熱銷爆款商品（預設 1.4x 零售利潤，每件享有批發差價）。顧客在您的商店下單後，好批直營倉庫一件代發出庫，免囤貨、零庫存風險！
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<InventoryIcon />}
                onClick={() => navigate('/admin/import')}
                sx={{
                  bgcolor: '#f59e0b',
                  color: '#0f172a',
                  fontWeight: 'bold',
                  '&:hover': { bgcolor: '#d97706' },
                  boxShadow: '0 4px 12px rgba(245,158,11,0.35)'
                }}
              >
                好批一鍵鋪貨 (2,710 款)
              </Button>
              <Button
                variant="outlined"
                startIcon={<LaunchIcon />}
                onClick={() => window.open('/', '_blank')}
                sx={{
                  borderColor: 'rgba(255,255,255,0.4)',
                  color: '#ffffff',
                  fontWeight: 'bold',
                  '&:hover': { borderColor: '#ffffff', bgcolor: 'rgba(255,255,255,0.1)' }
                }}
              >
                預覽前台商店
              </Button>
            </Box>
          </Box>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', my: 2 }} />

          {/* 3 步驟圖文導引卡片 */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 2, backdropFilter: 'blur(4px)', border: '1px solid rgba(255,255,255,0.12)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Box sx={{ bgcolor: '#3b82f6', color: '#fff', width: 24, height: 24, borderRadius: '50%', textAlign: 'center', lineHeight: '24px', fontSize: 13, fontWeight: 'bold' }}>1</Box>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#ffffff' }}>登入後台・個性化設定</Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', display: 'block', lineHeight: 1.5 }}>
                  進入「商店設定」修改個人密碼，自訂店名、上傳個人專屬 Logo 與更換首頁輪播橫幅。
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 2, backdropFilter: 'blur(4px)', border: '1px solid rgba(255,255,255,0.12)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Box sx={{ bgcolor: '#10b981', color: '#fff', width: 24, height: 24, borderRadius: '50%', textAlign: 'center', lineHeight: '24px', fontSize: 13, fontWeight: 'bold' }}>2</Box>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#ffffff' }}>好批一鍵鋪貨・自訂利潤</Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', display: 'block', lineHeight: 1.5 }}>
                  左側點擊「好批一鍵鋪貨」，搜尋好批 2,710+ 款現貨庫存，自訂加價倍率（如 1.4x），一鍵匯入上架！
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 2, backdropFilter: 'blur(4px)', border: '1px solid rgba(255,255,255,0.12)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Box sx={{ bgcolor: '#f59e0b', color: '#fff', width: 24, height: 24, borderRadius: '50%', textAlign: 'center', lineHeight: '24px', fontSize: 13, fontWeight: 'bold' }}>3</Box>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#ffffff' }}>分享網址・好批一件代發</Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', display: 'block', lineHeight: 1.5 }}>
                  將您的前台商店網址分享給顧客，客人下單結帳後，好批直營倉庫直接為您一件代發，現賺批發差價！
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('dashboard.today_orders')}
            value={stats.today_orders}
            icon={<TrendingIcon />}
            color="primary"
            subtitle={t('dashboard.today_orders_subtitle')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('dashboard.pending_orders')}
            value={stats.pending_orders + stats.paid_orders}
            icon={<PendingIcon />}
            color="warning"
            subtitle={t('dashboard.pending_subtitle', { pending: stats.pending_orders, paid: stats.paid_orders })}
            onClick={() => navigate('/admin/orders')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('dashboard.shipping_orders')}
            value={stats.shipping_orders}
            icon={<ShippingIcon />}
            color="info"
            subtitle={t('dashboard.shipping_subtitle')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('dashboard.total_revenue')}
            value={`$${(stats.total_revenue || 0).toLocaleString()}`}
            icon={<MoneyIcon />}
            color="success"
            subtitle={t('dashboard.total_revenue_subtitle', { count: stats.total_orders })}
          />
        </Grid>
      </Grid>

      {/* 方案資訊 Widget */}
      {billing && (
        <Paper
          sx={{ p: 2.5, mb: 3, cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
          onClick={() => navigate('/admin/billing')}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <RocketIcon color="primary" />
              <Box>
                <Typography variant="subtitle2" fontWeight="bold">
                  {billing.plan?.name || t('billing.free_trial')}
                </Typography>
                {billing.trial?.days_left !== null && !billing.subscription?.is_active ? (
                  <Typography variant="caption" color={billing.trial.days_left <= 3 ? 'error.main' : 'text.secondary'}>
                    {billing.trial.is_expired ? t('billing.trial_expired') : t('billing.trial_days_left', { days: billing.trial.days_left })}
                  </Typography>
                ) : billing.subscription?.is_active ? (
                  <Typography variant="caption" color="success.main">
                    {t('billing.subscription_active', { days: billing.subscription.days_left })}
                  </Typography>
                ) : null}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
              <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                <Typography variant="caption" color="text.secondary">{t('billing.products')}</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {billing.usage?.products}/{billing.usage?.products_limit === -1 ? '∞' : billing.usage?.products_limit}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                <Typography variant="caption" color="text.secondary">{t('billing.monthly_orders')}</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {billing.usage?.orders_monthly}/{billing.usage?.orders_limit === -1 ? '∞' : billing.usage?.orders_limit}
                </Typography>
              </Box>
              {billing.trial?.days_left !== null && billing.trial?.days_left <= 7 && !billing.subscription?.is_active && (
                <Button
                  variant="contained"
                  size="small"
                  color="warning"
                  startIcon={<WarningIcon />}
                  onClick={(e) => { e.stopPropagation(); navigate('/admin/billing'); }}
                >
                  {t('billing.upgrade')}
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      )}

      {/* Quick Actions & Recent Orders */}
      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {t('dashboard.quick_actions')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<OrderIcon />}
                onClick={() => navigate('/admin/orders')}
                sx={{ justifyContent: 'flex-start' }}
              >
                {t('dashboard.view_all_orders')}
              </Button>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<TrendingIcon />}
                onClick={() => navigate('/admin/analytics')}
                sx={{ justifyContent: 'flex-start' }}
              >
                {t('dashboard.analytics_report')}
              </Button>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<ShippingIcon />}
                onClick={() => navigate('/admin/orders?status=paid')}
                sx={{ justifyContent: 'flex-start' }}
              >
                {t('dashboard.pending_shipments')}
              </Button>
              <Divider sx={{ my: 1 }} />
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate('/admin/tenant-settings')}
                sx={{ justifyContent: 'flex-start' }}
              >
                {t('dashboard.system_settings')}
              </Button>
            </Box>
          </Paper>

          {/* Order Status Summary */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {t('dashboard.order_stats')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[
                { label: t('orders.status_pending'), value: stats.pending_orders, color: 'warning' },
                { label: t('orders.status_paid'), value: stats.paid_orders, color: 'info' },
                { label: t('orders.status_shipping', t('orders.status_shipped')), value: stats.shipping_orders, color: 'primary' },
                { label: t('orders.status_delivered'), value: stats.delivered_orders, color: 'success' }
              ].map(item => (
                <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">{item.label}</Typography>
                  <Chip
                    label={item.value}
                    size="small"
                    color={item.color}
                    sx={{ minWidth: 50 }}
                  />
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Recent Orders */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                {t('dashboard.recent_orders')}
              </Typography>
              <Button size="small" onClick={() => navigate('/admin/orders')}>
                {t('common.view_all')}
              </Button>
            </Box>

            {recentOrders.length === 0 ? (
              <Typography color="text.secondary" textAlign="center" py={4}>
                {t('dashboard.no_orders')}
              </Typography>
            ) : (
              <Box>
                {recentOrders.map((order, index) => (
                  <Box key={order.order_no}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 2,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                        borderRadius: 1,
                        px: 1
                      }}
                      onClick={() => navigate(`/admin/orders?search=${order.order_no}`)}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {order.order_no}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {order.customer_name} - {order.customer_phone}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {new Date(order.created_at).toLocaleString('zh-TW')}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          ${order.total_amount}
                        </Typography>
                        <Chip
                          label={statusLabels[order.status] || order.status}
                          size="small"
                          color={statusColors[order.status] || 'default'}
                        />
                      </Box>
                    </Box>
                    {index < recentOrders.length - 1 && <Divider />}
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;
